import { Injectable } from "@angular/core";
import { IUseCase } from "@karma-games/angular-devkit/src/lib/interfaces";
import { combineLatest, distinctUntilChanged, Observable, of, switchMap } from "rxjs";
import { EBetButtonState, IBetButtonState, IBetPanelAccumulatedValueData } from "../interfaces";
import { GetUserBetsUseCase } from "../../core/round/domain/get-user-bets.use-case";
import { GetRoundStateUseCase } from "../../core/round/domain/get-round-state.use-case";
import { ERoundState } from "../../core/round/interfaces";
import { betButtonStates } from "../constants";
import { IAutoplayOptions, IUserBet } from "../../core/round/store/interfaces";
import { map } from "rxjs/operators";
import { GetAutoplayUseCase } from "src/app/core/round/domain/get-autoplay-options.use-case";
import { GetBetActionInProgressUseCase } from "./get-bet-action-in-progress.use-case";
import _isEqual from "lodash/isEqual";

@Injectable()
export class GetBetButtonStateUseCase implements IUseCase<number, Observable<IBetButtonState>> {
    constructor(private getUserBetsUseCase: GetUserBetsUseCase,
                private getRoundStateUseCase: GetRoundStateUseCase,
                private getAutoplayUseCase: GetAutoplayUseCase,
                private getBetActionInProgressUseCase: GetBetActionInProgressUseCase) {
    }

    execute(betIndex: number): Observable<IBetButtonState> {
        return combineLatest([this.getUserBetsUseCase.execute(),
            this.getBetActionInProgressUseCase.execute(betIndex),
            this.getRoundStateUseCase.execute(),
            this.getAutoplayUseCase.execute(betIndex)
                .pipe(distinctUntilChanged((previous, current) => {
                    return previous?.roundCount === current?.roundCount
                }))])
            .pipe(distinctUntilChanged((prev: [IUserBet[], boolean, ERoundState, (IAutoplayOptions | undefined)],
                                          curr: [IUserBet[], boolean, ERoundState, (IAutoplayOptions | undefined)]) => {
                return _isEqual(prev[0], curr[0]) && prev[1] === curr[1] && prev[2] === curr[2] && _isEqual(prev[3], curr[3]);
            }), map(([userBets, actionInProgress, roundState, autoplayOptions]) => {
                    const bet: IUserBet | undefined = userBets.find(b => b.betIndex === betIndex);
                    return [bet ?? {
                        amount: undefined,
                        roundId: undefined,
                        accumulatedValue: undefined,
                        multiplier: undefined,
                        betIndex
                    }, actionInProgress, roundState, autoplayOptions];
                }),
                switchMap(([userBet, actionInProgress, roundState, autoplayOptions]:
                               [IUserBet, boolean, ERoundState, (IAutoplayOptions | undefined)]) => {
                    const accumulatedValue: IBetPanelAccumulatedValueData = {
                        accumulatedAmount: userBet.accumulatedValue ?? undefined,
                        roundId: userBet.roundId ?? undefined,
                        multiplier: userBet.multiplier ?? undefined,
                        autoplayRoundCount: autoplayOptions ? autoplayOptions.roundCount : undefined,
                        firstHalfCashedOut: userBet.firstHalfCashedOut
                    };
                    switch (roundState) {
                        case ERoundState.Idle:
                            if (accumulatedValue.accumulatedAmount) {
                                return of({
                                    ...betButtonStates[EBetButtonState.Betted],
                                    ...accumulatedValue,
                                    actionInProgress
                                });
                            } else {
                                return of({
                                    ...betButtonStates[EBetButtonState.NoBet],
                                    ...accumulatedValue,
                                    actionInProgress
                                });
                            }
                        case ERoundState.Playing:
                            if (accumulatedValue.accumulatedAmount && accumulatedValue.roundId) {
                                return of({
                                    ...betButtonStates[EBetButtonState.RoundPlaying],
                                    ...accumulatedValue,
                                    actionInProgress
                                });
                            } else if (accumulatedValue.accumulatedAmount || (!!autoplayOptions && autoplayOptions.roundCount > 0)) {
                                return of({
                                    ...betButtonStates[EBetButtonState.BetPrepared],
                                    ...accumulatedValue,
                                    actionInProgress
                                });
                            } else {
                                return of({
                                    ...betButtonStates[EBetButtonState.BetNotPrepared],
                                    ...accumulatedValue,
                                    actionInProgress
                                });
                            }
                        case ERoundState.Ended:
                            if (accumulatedValue.accumulatedAmount && accumulatedValue.roundId && (!autoplayOptions || autoplayOptions.roundCount <= 0)) {
                                return of({
                                    ...betButtonStates[EBetButtonState.BetNotPrepared],
                                    ...accumulatedValue,
                                    actionInProgress
                                });
                            } else if (accumulatedValue.accumulatedAmount) {
                                return of({
                                    ...betButtonStates[EBetButtonState.BetPrepared],
                                    ...accumulatedValue,
                                    actionInProgress
                                });
                            } else if (!!autoplayOptions && autoplayOptions.roundCount > 0) {
                                return of({
                                    ...betButtonStates[EBetButtonState.Betted],
                                    ...accumulatedValue,
                                    actionInProgress
                                });
                            } else {
                                return of({
                                    ...betButtonStates[EBetButtonState.BetNotPrepared],
                                    ...accumulatedValue,
                                    actionInProgress
                                });
                            }
                        default:
                            return of({
                                ...betButtonStates[EBetButtonState.BetNotPrepared],
                                ...accumulatedValue,
                                actionInProgress
                            });
                    }
                }));
    }
}
